import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    HashRouter as Router,
    Routes,
    Route,
    Navigate,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "react-hot-toast";
import { serverStore } from "./stores/server.store";
import { BugTrackerView } from "./views/BugTrackerView";
import { userStore } from "./stores/user.store";
import { backupStore } from "./stores/backup.store";
import { systemStore } from "./stores/system.store";
import { bugStore } from "./stores/bug.store";
import { apiFetch, getToken, clearToken } from "./utils/api";

import { AppShell } from "./components/shell/AppShell";
import { ServerBoard } from "./components/board/ServerBoard";
import { ServerWorkspace } from "./components/workspace/ServerWorkspace";

import EditServerModal from "./components/modals/EditServerModal";
import { ServerSetupWizard } from "./components/server/ServerSetupWizard";
import DeleteServerModal from "./components/modals/DeleteServerModal";
import CloudConfigModal from "./components/modals/CloudConfigModal";
import UserModal from "./components/modals/UserModal";
import ForcePasswordResetModal from "./components/modals/ForcePasswordResetModal";
import ResetPasswordModal from "./components/modals/ResetPasswordModal";
import { LogConsoleModal } from "./components/modals/LogConsoleModal";
import { ConfirmModal } from "./components/modals/ConfirmModal";
import { AiAssistant } from "./components/ai/AiAssistant";
import { SystemResourcesPanel } from "./components/server/SystemResourcesPanel";
import { UpdateBanner } from "./components/shell/UpdateBanner";
import LoginScreen from "./components/auth/LoginScreen";
import { can } from '@/utils/permissions';

const SettingsTab = React.lazy(() => import("./components/tabs/SettingsTab").then((m) => ({ default: m.SettingsTab })));
const StorageTab = React.lazy(() => import("./components/tabs/StorageTab").then((m) => ({ default: m.StorageTab })));
const UsersTab = React.lazy(() => import("./components/tabs/UsersTab").then((m) => ({ default: m.UsersTab })));
const SystemTab = React.lazy(() => import("./components/tabs/SystemTab").then((m) => ({ default: m.SystemTab })));
const AuditLogTab = React.lazy(() => import("./components/tabs/AuditLogTab"));
const OverallStatsTab = React.lazy(() => import("./components/tabs/OverallStatsTab").then((m) => ({ default: m.OverallStatsTab })));
const FundingTab = React.lazy(() => import("./components/tabs/FundingTab").then((m) => ({ default: m.FundingTab })));
const AiDashboard = React.lazy(() => import("./components/ai/AiDashboard").then((m) => ({ default: m.AiDashboard })));

const RouteSync = observer(() => {
    const location = useLocation();
    const navigate = useNavigate();

    // QoL: gewaehlte Startseite (Profil -> Bedienung) EINMAL pro Sitzung
    // ansteuern - nur wenn der Nutzer auf dem Board (/) gelandet ist.
    const uid = userStore.currentUser?.id;
    useEffect(() => {
        if (!uid) return;
        const sp = userStore.currentUser?.preferences?.start_page;
        if (!sp || sp === "/" || sessionStorage.getItem("gm_startpage_done")) return;
        sessionStorage.setItem("gm_startpage_done", "1");
        if (location.pathname === "/") navigate(sp);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid]);

    useEffect(() => {
        const match = location.pathname.match(/^\/server\/([^/]+)/);
        if (match && match[1] && serverStore.selectedServerId !== match[1]) {
            serverStore.selectedServerId = match[1];
        }
        const path = location.pathname;
        if (path === "/") serverStore.activeTab = "overview";
        else if (path.includes("/console")) serverStore.activeTab = "console";
        else if (path.includes("/logs")) serverStore.activeTab = "logs";
        else if (path.includes("/files")) serverStore.activeTab = "files";
        else if (path.includes("/software")) serverStore.activeTab = "software";
        else if (path.includes("/announcements")) serverStore.activeTab = "announcements";
        else if (path.includes("/settings")) serverStore.activeTab = "settings";
    }, [location]);
    return null;
});

const ProtectedRoute = ({ children, requiredPermission }: { children: React.ReactNode; requiredPermission?: string }) => {
    const { currentUser } = userStore;
    if (!currentUser) return <Navigate to="/" />;
    if (requiredPermission && !can(currentUser, requiredPermission)) {
        return <Navigate to="/" />;
    }
    return <>{children}</>;
};

const PageFallback = () => (
    <div className="flex h-full items-center justify-center py-24">
        <Loader2 size={40} className="text-ember animate-spin" />
    </div>
);

const AppContent = observer(() => {
    const { activeServer, showCreateModal, showDeleteModal, editingServer, handleSaveServer, performDeleteServer } = serverStore;
    const { currentUser, isUserModalOpen, handleSaveUser, editingUser, openNewUserModal, openEditModal, handleDeleteUser } = userStore;
    const { showCloudConfig } = backupStore;
    const [resetUser, setResetUser] = React.useState<any>(null);
    const navigate = useNavigate();

    return (
        <AppShell>
            <RouteSync />
            <ForcePasswordResetModal />
            <ResetPasswordModal isOpen={!!resetUser} onClose={() => setResetUser(null)} user={resetUser} />
            <UserModal isOpen={isUserModalOpen} onClose={() => (userStore.isUserModalOpen = false)} onSave={handleSaveUser} userToEdit={editingUser} />
            <Toaster position="top-right" toastOptions={{ style: { background: "#1a1a22", color: "#ececf2", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px" } }} />
            <ConfirmModal />
            <AiAssistant />
            <LogConsoleModal />
            <SystemResourcesPanel />
            <UpdateBanner />

            {showCreateModal && <ServerSetupWizard onClose={() => (serverStore.showCreateModal = false)} />}
            {showDeleteModal && activeServer && <DeleteServerModal serverName={activeServer.name} onClose={() => (serverStore.showDeleteModal = false)} onConfirm={async (deleteCloud) => { await performDeleteServer({ deleteCloudBackups: deleteCloud }); navigate("/"); }} />}
            {showCloudConfig && <CloudConfigModal onClose={() => (backupStore.showCloudConfig = false)} />}
            {editingServer && activeServer && <EditServerModal server={activeServer} onClose={() => (serverStore.editingServer = false)} onSave={handleSaveServer} />}

            <React.Suspense fallback={<PageFallback />}>
                <Routes>
                    <Route path="/" element={<ServerBoard />} />
                    <Route path="/server/:serverId/:section" element={<ServerWorkspace />} />
                    <Route path="/server/:serverId" element={<Navigate to="overview" replace relative="path" />} />

                    <Route path="/users" element={<div className="p-6 md:p-8"><UsersTab currentUser={currentUser} openNewUserModal={openNewUserModal} openEditModal={openEditModal} setResetUser={setResetUser} handleDeleteUser={handleDeleteUser} /></div>} />
                    {/* Backups sind jetzt pro Server (Server-Ansicht → Verwaltung → Backups). Alte Links umleiten. */}
                    <Route path="/backups" element={<Navigate to="/" replace />} />
                    <Route path="/storage" element={<div className="p-6 md:p-8">{can(currentUser, 'can_manage_storage') ? <StorageTab /> : <Navigate to="/" />}</div>} />
                    <Route path="/system" element={<div className="p-6 md:p-8"><SystemTab currentUser={currentUser} /></div>} />
                    <Route path="/stats" element={<div className="p-6 md:p-8"><ProtectedRoute requiredPermission="can_view_player_stats"><OverallStatsTab /></ProtectedRoute></div>} />
                    <Route path="/audit" element={<div className="p-6 md:p-8"><ProtectedRoute requiredPermission="can_view_audit_logs"><AuditLogTab /></ProtectedRoute></div>} />
                    <Route path="/bugs" element={<div className="p-6 md:p-8"><BugTrackerView /></div>} />
                    <Route path="/settings" element={<div className="p-6 md:p-8"><SettingsTab /></div>} />
                    <Route path="/funding" element={<div className="p-6 md:p-8"><FundingTab /></div>} />
                    <Route path="/ai-dashboard" element={<div className="h-full p-4"><ProtectedRoute requiredPermission="can_view_ai_dashboard"><AiDashboard /></ProtectedRoute></div>} />

                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </React.Suspense>
        </AppShell>
    );
});

const App = observer(() => {
    const { currentUser } = userStore;
    const [isCheckingSession, setIsCheckingSession] = useState(true);

    useEffect(() => {
        // Session-Check mit Augenmass: NUR 401/403 bedeuten "Token ungueltig".
        // 429 (Rate-Limit), 5xx oder Netzwerkfehler duerfen NICHT ausloggen -
        // dann kurz retryen und notfalls den User aus dem JWT rekonstruieren,
        // damit das Panel nutzbar bleibt, bis die API wieder antwortet.
        const decodeJwtUser = (token: string) => {
            try {
                const part = token.split(".")[1];
                if (!part) return null;
                const payload = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
                if (!payload?.username) return null;
                return { id: payload.id, username: payload.username, role: payload.role, permissions: payload.permissions || {}, preferences: {} } as any;
            } catch { return null; }
        };

        const checkSession = async () => {
            const token = getToken();
            if (token) {
                for (let attempt = 0; attempt < 3; attempt++) {
                    try {
                        const controller = new AbortController();
                        const timeoutId = setTimeout(() => controller.abort(), 5000);
                        const res = await apiFetch("/api/auth/me", { signal: controller.signal });
                        clearTimeout(timeoutId);
                        if (res.ok) {
                            const data = await res.json();
                            userStore.currentUser = data.user;
                            break;
                        }
                        if (res.status === 401 || res.status === 403) {
                            clearToken();
                            break;
                        }
                        // 429/5xx: nochmal versuchen, dann JWT-Fallback
                    } catch (error) {
                        if (error instanceof Error && error.name !== "AbortError") console.error("Session check failed:", error);
                    }
                    if (attempt === 2) {
                        const fallback = decodeJwtUser(token);
                        if (fallback) userStore.currentUser = fallback;
                        // Kein clearToken: Token ist evtl. voellig gueltig, nur die API gerade dicht.
                    } else {
                        await new Promise((r) => setTimeout(r, 1500));
                    }
                }
            }
            setIsCheckingSession(false);
        };
        checkSession();
    }, []);

    useEffect(() => {
        if (!currentUser) return;
        const init = async () => {
            await serverStore.fetchData();
            serverStore.checkAllUpdates();
            backupStore.fetchActiveRestores();
            bugStore.fetchBugCount();
            systemStore.fetchMaintenanceStatus(); // vor dem Login bewusst übersprungen
            // Kassen-Badge: offene Budget-Abstimmungen ohne eigene Stimme
            import("./stores/funding.store").then((m) => m.fundingStore.fetchNotifications());
        };
        init();
    }, [currentUser]);

    if (isCheckingSession) {
        return (
            <div className="flex h-screen bg-ink items-center justify-center">
                <div className="flex flex-col items-center space-y-4">
                    <Loader2 size={44} className="text-ember animate-spin" />
                    <p className="text-fg-muted text-sm">Sitzung wird geprüft…</p>
                </div>
            </div>
        );
    }

    if (!currentUser) {
        return <LoginScreen onLogin={(u) => { userStore.currentUser = u; }} />;
    }

    return (
        <Router>
            <AppContent />
        </Router>
    );
});

export default App;





